import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import { ReactComponent as SvgSearch } from 'public/assets/icons/search.svg';
import {
  IDescriptionsResort, IResortPathInfo, ITrailMap, ResortOverviewTab,
} from '@/api/resort/types';
import { ILightboxImage, Lightbox } from '@/components/common/Lightbox';
import { MarkdownContent } from '@/components/common/MarkdownContent';
import { SeoSection } from '@/components/common/SeoSection';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { useAnalytics } from '@/tracking/analytics';

import { useIsMobile } from '@/util/useIsMobile';
import { useBrandfolderOptimizedImage } from '@/util/useBrandfolderOptimizedImage';

const trailMapButtonClass = [
  'relative inline-flex flex-col items-center self-center w-full max-w-[560px]',
  'mt-2.5 pb-3',
].join(' ');

const imageClass = [
  'block w-full max-h-[500px] object-cover',
  'rounded-lg appearance-none border-0',
  'hover:[@media(hover:hover)]:cursor-zoom-in hover:[@media(hover:hover)]:opacity-50',
  'max-md:h-[300px]',
].join(' ');

const expandBlockIconClass = [
  'hidden',
  'max-md:absolute max-md:inset-0 max-md:flex max-md:items-center max-md:justify-center',
  'max-md:[&>svg]:-mt-[10%] max-md:[&>svg]:w-1/2 max-md:[&>svg]:h-1/2',
  'max-md:[&>svg]:fill-[#11ABD8] max-md:[&>svg]:opacity-50',
].join(' ');

const embedContainerClass = [
  'mt-6',
  '[&_iframe]:w-full [&_iframe]:min-h-[600px] [&_iframe]:border-0 [&_iframe]:rounded-lg',
  'max-md:mt-4 max-md:[&_iframe]:min-h-[360px]',
].join(' ');

export type IProps = {
  trailMap: ITrailMap;
  resort: IResortPathInfo;
  descriptionTabs?: Array<IDescriptionsResort>;
};

const addCtrlZoomToLiveUrl = (url: string | null | undefined) => {
  if (!url) {
    return null;
  }

  const urlObject = new URL(url);

  if (urlObject.searchParams.get('ctrlZoom') === 'true') {
    return url;
  }

  urlObject.searchParams.set('ctrlZoom', 'true');

  return urlObject.toString();
};

export const TrailMap = ({
  trailMap,
  resort,
  descriptionTabs,
}: IProps) => {
  const { t } = useOTSTranslation('common');
  const a = useAnalytics();
  const isMobile = useIsMobile();
  const optimizedImage = useBrandfolderOptimizedImage(trailMap.image, 2048);
  const optimizedThumbnail = useBrandfolderOptimizedImage(trailMap.thumbnail);
  const { liveUrl } = trailMap;
  const isLiveUrl = Boolean(liveUrl && !liveUrl.toLowerCase().endsWith('.jpg'));
  const processedLiveUrl = addCtrlZoomToLiveUrl(liveUrl);

  const images: Array<ILightboxImage> = [{
    title: `${resort.title}`,
    alt: `${resort.title}`,
    src: optimizedImage ?? '',
  }];

  const terrainTab = useMemo(() => descriptionTabs?.find((ot) => ot.tab === ResortOverviewTab.terrain), [descriptionTabs]);

  const [lightBoxIndex, setLightBoxOpen] = useState<boolean>(false);

  const openImage = useCallback((e: React.SyntheticEvent) => {
    if (isMobile) { return; }

    e.preventDefault();
    setLightBoxOpen(true);
    a.expandImage({ content: 'Trail Map' });
  }, [images]);

  return (
    <>
      <SeoSection title={t('resort.trailMap.seoTitle', { resort: resort.title })}>
        {t('resort.trailMap.seoText', { resort: resort.title })}
      </SeoSection>
      <div className="mt-6 px-3">
        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
        {trailMap.image && trailMap.thumbnail && (
          <div className="text-center">
            <a
              href={optimizedImage}
              onClick={openImage}
              className={trailMapButtonClass}
              tabIndex={-1}
              target="_blank"
              rel="noreferrer nofollow"
            >
              <img
                src={optimizedThumbnail}
                alt={t('resort.trailMap.seoTitle', { resort: resort.title })}
                className={imageClass}
              />
              <span className="mt-2.5 text-primary">{t('resort.trailMap.expand')}</span>
              <span className={expandBlockIconClass}><SvgSearch /></span>
            </a>
          </div>
        )}
        {isLiveUrl && (
          <div className={embedContainerClass}>
            <iframe
              src={processedLiveUrl ?? undefined}
              title={`${resort.title} trail map`}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        )}
        {terrainTab && (
          <MarkdownContent text={terrainTab.description} />
        )}
      </div>
      {lightBoxIndex && (
        <Lightbox onClose={() => setLightBoxOpen(false)} images={images} index={0} />
      )}
    </>
  );
};
